import { action, computed, observable, ObservableMap } from 'mobx';

import { makePaginated, Paginated } from '../../adl-gen/common';
import { DbKey, WithDbId } from '../../adl-gen/common/db';
import { TableQuery } from '../../adl-gen/common/tabular';
import { Competency, Question, Skill } from '../../adl-gen/totalinteraction/futurefit/db';
import { Entry, RepositoryEntries, RepositoryEntrySet } from '../../adl-gen/totalinteraction/futurefit/frontend';
import {
  makeRepositoryPutReq,
  makeRepositoryReq,
  RepositoryPutEntry,
  RepositoryPutReq,
  RepositoryReq
} from '../../adl-gen/totalinteraction/futurefit/requests';
import { fromPromise, MaybeLoading } from '../../loader/loader';
import { Service } from '../../service/service';
import { LoggedIn, LoggedInSuper } from '../../stores/identity-store';
import { QuestionListEntry } from '../ui/components/lists/question-list/question-list-view';
//import { identity } from "../util/admin-story-helpers";

import { DataStore, TransportLayer } from './datastore';

export interface QuestionCompetencySkillsMap {
  competencies: Map<DbKey<Competency>, Competency>;
  skills: Map<DbKey<Skill>, Skill>;
  skillsByCompetency: Map<DbKey<Competency>, WithDbId<Skill>[]>;
  // questionsBySkill: Map<DbKey<Skill>, DbKey<Question>[]>;
}

class QuestionApi implements TransportLayer<Question> {
  kind: string = 'Question';
  constructor(protected readonly service: Service) {}
  create = async (item: Question): Promise<DbKey<Question>> => this.service.createQuestion(item);
  update = async (itemWithId: WithDbId<Question>): Promise<void> => this.service.updateQuestion(itemWithId);
  doDelete = async (id: DbKey<Question>): Promise<void> => this.service.deleteQuestion(id);
  query = async (q: TableQuery): Promise<Paginated<WithDbId<Question>>> => this.service.queryQuestions(q);

  querySkills = async (q: TableQuery): Promise<Paginated<WithDbId<Skill>>> => this.service.querySkills(q);
  queryCompetencies = async (q: TableQuery): Promise<Paginated<WithDbId<Competency>>> =>
    this.service.queryCompetencies(q);
  uploadRepositoryEntry = async (req: RepositoryPutReq): Promise<Entry> => this.service.uploadRepositoryEntry(req);
  listRepositoryEntries = async (req: RepositoryReq): Promise<RepositoryEntries> =>
    this.service.listRepositoryEntries(req);
}

export class QuestionStore extends DataStore<Question> {
  @observable list?: Promise<Paginated<QuestionListEntry>>;

  // @observable competencies?: Paginated<WithDbId<Competency>>
  // @observable skills?: Paginated<WithDbId<Skill>>
  @observable selectedEntry?: QuestionListEntry;
  // @observable skillId?: DbKey<Skill>
  @observable competencySkillsMap: Promise<QuestionCompetencySkillsMap>; // = {
  //     competencies: observable.map(new Map()),
  //     skills: observable.map(new Map()),
  //     skillsByCompetency: observable.map(new Map()),
  //     // questionsBySkill: observable.map(new Map())
  // }
  @observable repository: Promise<RepositoryEntrySet>;

  public static fromService = (
    service: Service,
    identity: LoggedIn | LoggedInSuper,
    skillId?: DbKey<Skill>,
    id?: DbKey<Question>
  ): QuestionStore => {
    // Currently only superUser can manage questions
    if (identity.kind !== 'super-logged-in') {
      throw new Error('Questions cannot be managed by Client Admins');
    }
    const store = new QuestionStore(new QuestionApi(service), identity, skillId, id);
    return store;
  };

  constructor(
    protected readonly transport: TransportLayer<Question>,
    protected readonly identity: LoggedIn | LoggedInSuper,
    protected readonly skillId?: DbKey<Skill>,
    protected readonly id?: DbKey<Question>
  ) {
    super(transport, identity, id);
    this.skillId = skillId;
    // Don't autoload all data, we might only want a subset - hence not in base class
    this.data = this.init(id);
    this.list = this.initList().then(list => {
      if (id) {
        this.selectedEntry = list.items.find(item => item.id === id);
      }
      return list;
    });
    this.competencySkillsMap = this.initMaps();
    this.repository = this.initRepository();
  }

  private readonly initList = (): Promise<Paginated<QuestionListEntry>> => {
    return this.loadList();
  };

  private readonly loadList = async (): Promise<Paginated<QuestionListEntry>> => {
    if (this.authorized) {
      const results = await this.queryList(this.queryAll);
      return results;
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly initMaps = async (): Promise<QuestionCompetencySkillsMap> => {
    const competencies = await (this.transport as QuestionApi).queryCompetencies(this.queryAll);
    const skills = await (this.transport as QuestionApi).querySkills(this.queryAll);
    return this.loadMaps(competencies, skills);
  };

  @action
  private readonly loadMaps = (
    competencyList: Paginated<WithDbId<Competency>>,
    skillList: Paginated<WithDbId<Skill>>
    // questionList: Paginated<WithDbId<Question>>
  ): QuestionCompetencySkillsMap => {
    const competencies: ObservableMap<DbKey<Competency>, Competency> = observable.map(new Map());
    const skills: ObservableMap<DbKey<Skill>, Skill> = observable.map(new Map());
    const skillsByCompetency: ObservableMap<DbKey<Competency>, WithDbId<Skill>[]> = observable.map(new Map());
    // const questionsBySkill: Map<DbKey<Skill>, DbKey<Question>[]> = new Map()

    for (const skill of skillList.items) {
      skills.set(skill.id, skill.value);
    }

    for (const competency of competencyList.items) {
      competencies.set(competency.id, competency.value);
      const competencySkills = skillList.items.filter(skill => skill.value.competencyId === competency.id);
      skillsByCompetency.set(competency.id, competencySkills);
    }
    // for (const skill of skillList.items) {
    //     const questions = questionList.items
    //         .filter(q => q.value.skillId === skill.id)
    //         .map(q => q.id);
    //     questionsBySkill.set(skill.id, questions);
    // }
    return {
      competencies,
      skills,
      skillsByCompetency
      // questionsBySkill
    };
  };

  // _loadForSkill = async (): Promise<Paginated<WithDbId<Question>>> => {
  //   if (this.skillId) {
  //     return this.query(this.queryId(this.skillId, "skillId"))
  //   } else {
  //     return this.query(this.queryAll)
  //   }
  // }

  /// S3 Actions
  private readonly initRepository = async (): Promise<RepositoryEntrySet> => {
    const repository: RepositoryEntrySet = {};
    if (this.authorized) {
      return this.loadRepositories(repository);
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly loadRepositoryEntries = async (path: string): Promise<RepositoryEntrySet> => {
    return {
      [path]: await this.listRepositoryEntries(
        makeRepositoryReq({
          appClientId: this.identity.appClient.id,
          path
        })
      )
    };
  };

  private readonly loadRepositories = async (repository: RepositoryEntrySet): Promise<RepositoryEntrySet> => {
    try {
      const questionDataPath = 'data-images/questions/content';

      const repos = await this.loadRepositoryEntries(questionDataPath);

      return { ...repository, ...repos };
    } catch (e) {
      return Promise.reject(`Unable to load default repositories: ${(e as Error).message}`);
    }
  };

  private readonly listRepositoryEntries = async (req: RepositoryReq): Promise<RepositoryEntries> => {
    if (this.isSuperUser) {
      return (this.transport as QuestionApi).listRepositoryEntries(req);
    } else {
      return Promise.reject('Not authorized');
    }
  };

  public uploadRepositoryEntry = async (entry: RepositoryPutEntry): Promise<Entry> => {
    if (this.isSuperUser) {
      return (this.transport as QuestionApi).uploadRepositoryEntry(
        makeRepositoryPutReq({
          // NOTE: This appClientId has no effect on the upload, not 100% sure why it's there
          appClientId: this.identity.appClient.id,
          entry
        })
      );
    } else {
      return Promise.reject('Not authorized');
    }
  };

  @computed
  public get loadingRepository(): MaybeLoading<RepositoryEntrySet> {
    if (this.isSuperUser) {
      return fromPromise(this.repository);
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  @computed
  public get loadingList(): MaybeLoading<Paginated<QuestionListEntry>> {
    if (this.isSuperUser) {
      if (this.list) {
        return fromPromise(this.list);
      } else {
        return { kind: 'loading' };
      }
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  @computed
  public get loadingCompetencySkillsMap(): MaybeLoading<QuestionCompetencySkillsMap> {
    if (this.isSuperUser) {
      return fromPromise(this.competencySkillsMap);
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  private readonly queryList = async (q: TableQuery): Promise<Paginated<QuestionListEntry>> => {
    const result = await this.transport.query(q);
    return makePaginated({
      items: result.items.map(item => ({
        id: item.id,
        ...item.value,
        identity: this.isSuperUser ? 'super' : 'client-admin'
      })),
      current_offset: result.current_offset,
      total_size: result.total_size
    });
  };
}



// WEBPACK FOOTER //
// ./src/admin/stores/question-store.ts